import { Icon, Typography } from "@/components/atoms";
import { getCardBrandLogo, getCardLastFour, getCardNicknameDisplay, isCreditCard } from "@/components/organisms/PaymentSettings/helpers";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";

/**
 * The card to use when nobody picks one — which, in this wizard, is always.
 *
 * Prefer the flagged default, fall back to the first. Matches PaymentPanel's preselect and the
 * server's own fallback — they have to agree, or the fare quoted isn't the fare billed. Note the
 * older booking paths just take [0] and ignore the flag, which is the bug this rule exists to avoid.
 */
export const preferredPaymentMethod = (methods: ClientPaymentMethod[]): ClientPaymentMethod | undefined =>
  methods.find((method) => method.default) ?? methods[0];

/**
 * Brand tile + name + masked digits, or the invoice equivalent (DEV-1215).
 *
 * Display only, deliberately. A bulk import always uses the account's default and the wizard offers
 * no way to change it — the booking sheet's picker ends in ConfirmCardModal, a per-booking "is this
 * the right card?" gate, and that promise doesn't survive being made once for fifty bookings.
 *
 * It's shown anyway because the card decides the fee: an international card is surcharged at 3.5%
 * against a domestic 1.7%, so two clients see different totals for the same trip. Naming it up front
 * is what makes the estimates on the review step explainable.
 */
export const PaymentMethodSummary = ({ method }: { method: ClientPaymentMethod }) => {
  const isCard = isCreditCard(method);

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-neutral-mid-gray bg-white">
        {isCard ? getCardBrandLogo(method.card_data?.issuer) : <Icon name="EmptyWallet" size="sm" className="text-neutral-dark-gray" />}
      </span>
      <Typography variant="small" className="truncate text-neutral-black">
        {isCard ? `${getCardNicknameDisplay(method)} •••• ${getCardLastFour(method.card_data)}` : "Company Invoicing"}
      </Typography>
    </span>
  );
};
